import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import TimelineDot from './TimelineDot';

interface CurrentVersionHeaderProps {
  label?: string;
  isSelected?: boolean;
  isInPreview?: boolean;
  isFirstInSelection?: boolean;
  isFirstInPreview?: boolean;
  isHovered?: boolean;
  onClick?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
}

// eslint-disable-next-line complexity
export const CurrentVersionHeader: React.FC<CurrentVersionHeaderProps> = ({
  label = 'Current version',
  isSelected = false,
  isInPreview = false,
  isFirstInSelection = false,
  isFirstInPreview = false,
  isHovered = false,
  onClick,
  onMouseDown,
}) => {
  const currentVersionColor = useColorModeValue('cyan.500', 'cyan.200');
  const selectedBgColor = useColorModeValue('blue.50', 'blue.800');
  const previewBgColor = useColorModeValue('gray.50', 'gray.700');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const selectedBorderColor = useColorModeValue('blue.500', 'blue.300');

  // Calculate background color based on state
  let bgColor;
  if (isInPreview) {
    bgColor = previewBgColor;
  } else if (isSelected) {
    bgColor = selectedBgColor;
  }

  // Calculate border styles - always rounded on top when selected/preview
  // and the bottom is flush with the first event (no radius, no visible border)
  const borderTopRadius = 'lg';
  const borderTop =
    (isSelected && isFirstInSelection) || (isInPreview && isFirstInPreview)
      ? '2px solid'
      : 'none';
  const borderBottom = 'none';

  return (
    <Flex
      position="relative"
      onClick={onClick}
      onMouseDown={onMouseDown}
      cursor={onClick ? 'pointer' : 'default'}
    >
      {/* Current Version dot */}
      <Box
        position="absolute"
        left="16px"
        top={3}
        transform="translateX(-50%)"
        zIndex={2}
      >
        <TimelineDot isCurrentVersion />
      </Box>

      {/* Current Version content */}
      <Box
        width="100%"
        paddingLeft={9}
        paddingRight={4}
        backgroundColor={
          isHovered && !isSelected && !isInPreview ? hoverBgColor : bgColor
        }
        borderTopRadius={borderTopRadius}
        borderBottomRadius={0}
        borderTop={borderTop}
        borderBottom={borderBottom}
        paddingTop={borderTop === '2px solid' ? '7px' : '9px'}
        borderColor={selectedBorderColor}
        marginBottom={0}
        _hover={
          !isSelected && onClick && !isHovered
            ? { backgroundColor: hoverBgColor, borderTopRadius: 'lg' }
            : undefined
        }
        transition="background-color 0.2s"
      >
        <Text fontWeight="bold" fontSize="sm" color={currentVersionColor}>
          {label}
        </Text>
      </Box>
    </Flex>
  );
};

export default CurrentVersionHeader;
